
import { useState, useEffect } from 'react';
import { User as UserType } from '@/types/userRole';

export interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

const checkProfileCompletion = (user: UserType): boolean => {
  return !!(
    user.name &&
    user.email &&
    user.address &&
    user.city &&
    user.state &&
    user.zip &&
    user.country &&
    user.phone
  );
};

const checkPaymentMethodsCompletion = (user: UserType): boolean => {
  // Updated to check if the user has any payment details
  // Either account number or email that could be used for PayPal
  return !!(user.accountNumber || user.email); // Using email as a fallback since paypalEmail doesn't exist
};

const checkSocialReferralSent = (user: UserType): boolean => {
  // Check if the user has sent a referral via social media
  // This is mockup - in a real app, you'd track this in the database
  return !!localStorage.getItem(`social-referral-sent-${user.id}`);
};

export const useTaskNotifications = (user: UserType | null) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  
  useEffect(() => {
    if (!user) return;
    
    // Define the tasks
    const initialTasks = [
      {
        id: 'profile',
        title: 'Complete your profile',
        description: 'Fill in your personal details for better service',
        completed: checkProfileCompletion(user)
      },
      {
        id: 'payment',
        title: 'Add payment receiving method',
        description: 'Set up how you would like to receive payments',
        completed: checkPaymentMethodsCompletion(user)
      },
      {
        id: 'social-referral',
        title: 'Send your first referral',
        description: 'Share your referral link with friends via social media',
        completed: checkSocialReferralSent(user)
      }
    ];
    
    setTasks(initialTasks);
  }, [user]);
  
  // Load saved task state from localStorage
  useEffect(() => {
    if (!user) return;
    
    const savedTasks = localStorage.getItem(`tasks-${user.id}`);
    if (savedTasks) {
      try {
        const parsedTasks = JSON.parse(savedTasks);
        setTasks(prev => 
          prev.map(task => {
            const savedTask = parsedTasks.find((t: Task) => t.id === task.id);
            return savedTask ? { ...task, completed: savedTask.completed } : task;
          })
        );
      } catch (e) {
        console.error('Failed to parse saved tasks', e);
      }
    }
  }, [user]);
  
  // Save task state to localStorage when it changes
  useEffect(() => {
    if (!user || tasks.length === 0) return;
    
    localStorage.setItem(`tasks-${user.id}`, JSON.stringify(tasks));
  }, [tasks, user]);
  
  const markTaskComplete = (taskId: string) => {
    setTasks(prev => 
      prev.map(task => 
        task.id === taskId ? { ...task, completed: true } : task
      )
    );
    
    // If social referral task is marked complete, store it in localStorage
    if (taskId === 'social-referral' && user) {
      localStorage.setItem(`social-referral-sent-${user.id}`, 'true');
    }
  };
  
  return { tasks, markTaskComplete };
};
